import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Image as ImageIcon } from 'lucide-react';
import type { Api } from '../adapters/Api';
import { shieldEventFamily, type ShieldWatchEvent } from '../domain/intelligence';
import { useI18n, type TranslationKey } from '../i18n';
import { formatRelative } from './status';

// Feed de ACTIVIDAD de la flota (0.6.28): la versión persistente de los
// correos. Todo aviso que llegó (o llegará) por email se puede repasar aquí:
// ataques a tu ciudad (Auto Shield) y cambios de la vigilancia (Shield
// Monitor), mezclados, el más reciente arriba, cada uno con su captura del
// momento si aún la conserva (5 últimas por bot y familia).
//
// Vive en la página principal de flota a propósito: los eventos de Auto Shield
// son raros e importantes (tipo notificación, no configuración) y no merecen
// pestaña propia; el historial de la vigilancia sigue además en su panel de
// Inteligencia, contextual a su configuración.

const STATE_META: Record<string, { icon: string; cls: string; key: TranslationKey }> = {
  protected: { icon: '🛡️', cls: 'ok', key: 'activity.protected' },
  attack_halted: { icon: '🚨', cls: 'down', key: 'activity.attackHalted' },
  shielded: { icon: '', cls: 'ok', key: 'shieldWatch.stateShielded' },
  confirming: { icon: '', cls: 'warn', key: 'shieldWatch.stateConfirming' },
  down: { icon: '', cls: 'down', key: 'shieldWatch.stateDown' },
  moved: { icon: '', cls: 'warn', key: 'shieldWatch.stateMoved' },
  unknown: { icon: '', cls: 'warn', key: 'shieldWatch.stateNoDetector' }
};

export function ActivityFeed({
  api,
  nameOf
}: {
  api: Api;
  nameOf: (instanceId: string) => string;
}) {
  const { t } = useI18n();
  const query = useQuery({
    queryKey: ['shieldWatchHistory'],
    queryFn: () => api.dlShieldWatchHistory(15),
    refetchInterval: 20000,
    retry: false
  });
  const [shown, setShown] = useState<ShieldWatchEvent | null>(null);
  const capture = useQuery({
    queryKey: ['shieldWatchCapture', shown?.instanceId, shown?.stamp, shown?.state],
    queryFn: () => api.dlShieldWatchCapture(shown!.instanceId, shown!.stamp, shown!.state),
    enabled: !!shown,
    staleTime: 5 * 60000,
    retry: false
  });
  // Plegado por defecto a 3 eventos (feedback del dueño 2026-08-05: la lista
  // completa empujaba la descarga fuera de pantalla). "Ver todo" lo despliega.
  const [expanded, setExpanded] = useState(false);
  const events = query.data ?? [];
  // Sin eventos no hay feed: un panel vacío solo añadiría ruido a la principal.
  if (!events.length) return null;
  const visible = expanded ? events : events.slice(0, 3);

  return (
    <section className="activity-feed">
      <h2>{t('activity.title')}</h2>
      <ul>
        {visible.map((event, index) => {
          const meta = STATE_META[event.state] ?? STATE_META.unknown;
          const city = shieldEventFamily(event.state) === 'city';
          return (
            <li key={`${event.at}-${index}`}>
              <span className={`intel-watch-dot intel-watch-${meta.cls}`} aria-hidden="true" />
              <strong className="activity-bot">{nameOf(event.instanceId)}</strong>
              <span className="activity-state">
                {meta.icon && `${meta.icon} `}
                {t(meta.key)}
              </span>
              {!city && event.x !== null && event.y !== null && (
                <span className="muted">({event.x},{event.y})</span>
              )}
              {event.notified && <span className="intel-watch-mailed">✉</span>}
              {event.hasCapture && (
                <button
                  type="button"
                  className="intel-watch-shot"
                  onClick={() => setShown(event)}
                  title={t('shieldWatch.captureOpen')}
                  aria-label={t('shieldWatch.captureOpen')}
                >
                  <ImageIcon size={14} aria-hidden="true" />
                </button>
              )}
              <time>{formatRelative(event.at, t)}</time>
            </li>
          );
        })}
      </ul>
      {events.length > 3 && (
        <button
          type="button"
          className="activity-toggle"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? t('activity.showLess')
            : t('activity.showAll', { count: String(events.length) })}
        </button>
      )}
      {shown && (
        <div
          className="intel-watch-viewer"
          role="dialog"
          aria-modal="true"
          onClick={() => setShown(null)}
        >
          <figure onClick={(e) => e.stopPropagation()}>
            <figcaption>
              {nameOf(shown.instanceId)}
              {' · '}
              {t((STATE_META[shown.state] ?? STATE_META.unknown).key)}
              {shieldEventFamily(shown.state) === 'watch' && shown.x !== null && shown.y !== null
                && ` (${shown.x},${shown.y})`}
              {' · '}
              {formatRelative(shown.at, t)}
            </figcaption>
            {capture.isLoading && <p className="muted">{t('shieldWatch.captureLoading')}</p>}
            {!capture.isLoading && capture.data && (
              <img src={capture.data} alt={t('shieldWatch.captureAlt')} />
            )}
            {!capture.isLoading && !capture.data && (
              <p className="muted">{t('shieldWatch.captureGone')}</p>
            )}
            <button type="button" onClick={() => setShown(null)}>
              {t('shieldWatch.captureClose')}
            </button>
          </figure>
        </div>
      )}
    </section>
  );
}

// Último evento de CIUDAD (Auto Shield) reciente de cada bot: alimenta el badge
// "🛡️ protegido hace Xh" de su tarjeta en la flota. 24 h de ventana — más allá
// deja de ser noticia y para eso está el feed.
export function recentCityEvents(events: ShieldWatchEvent[] | undefined): Map<string, ShieldWatchEvent> {
  const byBot = new Map<string, ShieldWatchEvent>();
  const cutoff = Date.now() - 24 * 3600 * 1000;
  for (const event of events ?? []) {
    if (shieldEventFamily(event.state) !== 'city') continue;
    if (Date.parse(event.at) < cutoff) continue;
    if (!byBot.has(event.instanceId)) byBot.set(event.instanceId, event); // ya viene desc
  }
  return byBot;
}
